import { useState, useCallback, useRef } from 'react';
import {
  validateFileType,
  validateFileSize,
  formatFileSize
} from '../../utils/fileValidation';
import styles from './FileUploadZone.module.css';

const FileUploadZone = ({
  accept,
  maxSize,
  multiple = false,
  onFileSelect,
  onError,
  disabled = false,
  className = '',
  placeholder = 'Click to select or drag and drop',
  hint
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState(null);

  const validateFile = useCallback((file) => {
    // Validate type
    const typeValidation = validateFileType(file, accept);
    if (!typeValidation.valid) {
      return typeValidation;
    }

    // Validate size
    const sizeValidation = validateFileSize(file, maxSize);
    if (!sizeValidation.valid) {
      return sizeValidation;
    }

    return { valid: true };
  }, [accept, maxSize]);

  const handleFiles = useCallback((files) => {
    setError(null);

    const fileList = Array.from(files);
    const filesToProcess = multiple ? fileList : fileList.slice(0, 1);

    // Validate all files
    const validFiles = [];
    const errors = [];

    filesToProcess.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (errors.length > 0) {
      const errorMessage = errors.join('; ');
      setError(errorMessage);
      onError?.(errorMessage);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      onFileSelect?.(multiple ? validFiles : validFiles[0]);
    }
  }, [multiple, validateFile, onFileSelect, onError]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, handleFiles]);

  const handleFileSelect = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setSelectedFiles([]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect?.(null);
  }, [onFileSelect]);

  const hasFiles = selectedFiles.length > 0;

  return (
    <div
      className={`${styles.zone} ${isDragging ? styles.dragging : ''} ${hasFiles ? styles.hasFiles : ''} ${disabled ? styles.disabled : ''} ${className}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        disabled={disabled}
        className={styles.input}
      />

      {!hasFiles ? (
        <>
          <div className={styles.icon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <p className={styles.placeholder}>{placeholder}</p>
          {hint && <p className={styles.hint}>{hint}</p>}
        </>
      ) : (
        <div className={styles.selectedFiles}>
          {selectedFiles.map((file, index) => (
            <div key={index} className={styles.fileItem}>
              <div className={styles.fileIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className={styles.fileInfo}>
                <p className={styles.fileName}>{file.name}</p>
                <p className={styles.fileSize}>{formatFileSize(file.size)}</p>
              </div>
            </div>
          ))}
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Clear
          </button>
        </div>
      )}

      {error && (
        <div className={styles.error}>{error}</div>
      )}
    </div>
  );
};

export default FileUploadZone;
